import React from "react";
import { FaCar, FaCheckCircle, FaTrash } from "react-icons/fa";
import { MdLocalCarWash } from "react-icons/md";
import { useTranslation } from "react-i18next";
import config from "../../config"; // Assuming config is still needed for icon URLs
import i18next from "i18next";

// Helper component for individual pricing cards
const SubscriptionCard = ({
  subscription,
  onSelect,
  onDelete,
  showDelete,
  isFeatured = false,
}) => {
  const { t, i18n } = useTranslation();
  const {
    name_en,
    name_ar,
    description_en, // Keeping these as they are in your data
    description_ar, // Keeping these as they are in your data
    icon,
    color,
    number_of_cars,
    number_of_washes,
    duration_days,
    cost,
    one_year_offer, // This is just a number based on your data
    // expiry_display_date, // Excluded as it's likely for an instance, not package detail
    // is_active,          // Excluded as it's a status, not a feature
    subscriptionServices, // This was an empty array in your example
  } = subscription;

  const currentName = i18n.dir() === "rtl" ? name_ar : name_en;
  // Use description if it's meaningful, otherwise consider hiding if it just duplicates the name
  const currentDescription =
    i18n.dir() === "rtl" ? description_ar : description_en;

  // Features based on your data
  const mainFeatures = [
    {
      label: t("CarsIncluded"),
      value: number_of_cars || t("Unlimited"),
      icon: <FaCar className="text-xl" />,
    },
    {
      label: t("Washes_per_cycle"),
      value: number_of_washes || t("Unlimited"),
      icon: <MdLocalCarWash className="text-xl" />,
    },
    {
      label: t("duration"),
      value: `${duration_days} ${t("days")}`,
      icon: null,
    },
  ];


  return (
    <div dir={i18next.language === "ar" ? "rtl" : "ltr"}
      className={`relative flex flex-col items-center p-8 border rounded-2xl shadow-xl transition-all duration-300 ease-in-out
        ${
          isFeatured
            ? "border-2 border-blue-600 transform scale-105 shadow-2xl bg-blue-50"
            : "border-gray-200 bg-white"
        }
        hover:shadow-xl hover:scale-[1.02] cursor-pointer`}
      onClick={() => onSelect && onSelect(subscription)}
      style={{ borderColor: isFeatured ? color || "#3B82F6" : "#E5E7EB" }} // Use subscription color for featured border
    >
      {isFeatured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">
          {t("Recommended")}
        </span>
      )}

      {showDelete && (
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-red-600 transition-colors duration-300 z-20"
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            onDelete(subscription);
          }}
          title={t("Delete")}
        >
          <FaTrash className="text-xl" />
        </button>
      )}

      {/* Icon and Name */}
      <div className="text-center mb-6">
        <h3 className="text-3xl font-extrabold text-gray-900 mb-2">
          {currentName}
        </h3>
        {currentDescription &&
          currentDescription !== currentName && ( // Only show description if it's different from the name
            <p className="text-gray-600 text-sm">{currentDescription}</p>
          )}
      </div>

      {/* Price */}
      <div className="mb-6 text-center">
        <p className="text-5xl font-extrabold text-green-700">
          {parseFloat(cost).toFixed(0)}{" "}
          <span className="text-xl text-gray-500">QAR</span>
        </p>
        {one_year_offer && ( // Display one_year_offer if it exists
          <p className="text-orange-500 font-semibold mt-2">
            {t("AnnualPlan")}: {one_year_offer} {t("QAR")}
          </p>
        )}
      </div>

      {/* Key Features (based on your numerical fields) */}
      <div className="w-full mb-6 text-gray-800">
        <ul className="space-y-3">
          {mainFeatures.map((feature, index) => (
            <li key={index} className="flex items-center text-lg">
              <div className="w-[336px] flex justify-between items-center flex-row gap-4">
                <span className="text-[#222222] font-['Inter'] opacity-75">
                {feature.label} :  <span className="font-semibold mr-2">{feature.value} </span>
                  
                </span>
                <div className="w-[24px] h-[24px] flex justify-center items-center flex-row">
                  <div>
                    <FaCheckCircle />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA Button */}
      <button
        className={`w-full py-3 rounded-lg text-lg font-bold transition-colors duration-300
          ${
            isFeatured
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-100 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
          }`}
        onClick={(e) => {
          e.stopPropagation(); // Prevent card click from double triggering
          onSelect && onSelect(subscription);
        }}
      >
        {t("ChoosePlan")}
      </button>
    </div>
  );
};

export default SubscriptionCard;
